import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";

import { useState } from "react";

import { api, money } from "../api";
import { MODE_MEANING } from "./modes";
import { Trigger } from "./Trigger";

/**
 * Every run, newest first.
 *
 * Polls only while something on the page is still in flight — `is_terminal`
 * comes from the server so the status vocabulary is not re-derived here, which
 * is how a dead row ends up polled forever.
 */
export function RunList() {
  const projects = useQuery({ queryKey: ["projects"], queryFn: api.projects });
  const [target, setTarget] = useState<string>("");
  const runs = useQuery({
    queryKey: ["runs"],
    queryFn: api.runs,
    refetchInterval: (query) =>
      query.state.data?.some((run) => !run.is_terminal) ? 5_000 : false,
  });

  // One flat list of project+service pairs, built once and used for the options,
  // the lookup and the dispatch alike. These were three separate expressions
  // before, and they disagreed: the options were generated per service while the
  // lookup matched only each project's *first* service and the dispatch ignored
  // the selection entirely — so choosing any other service ran the first one,
  // against a project the label never named.
  const targets = (projects.data ?? []).flatMap((project) =>
    project.services.map((service) => ({
      key: `${project.id}/${service.name}`,
      project,
      service,
    })),
  );
  // `??` and not `||`: falling back to the first entry when nothing is selected
  // is right on the first render, and wrong after that.
  const chosen = targets.find((t) => t.key === target) ?? targets[0];

  const trigger = chosen ? (
    <>
      {targets.length > 1 ? (
        <label className="picker">
          Project
          {/* Driven by `chosen.key`, not by `target`: `target` is "" until
              something is picked, which matches no option and renders the
              control blank while a run would dispatch against the fallback. */}
          <select value={chosen.key} onChange={(event) => setTarget(event.target.value)}>
            {targets.map((t) => (
              <option key={t.key} value={t.key}>
                {t.project.repo} · {t.service.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <p className="muted">
        {chosen.project.repo} is in <code>{chosen.project.write_mode}</code> mode
        {" — it "}
        {MODE_MEANING[chosen.project.write_mode] ?? "may write to the repository"}.
      </p>
      <Trigger projectId={chosen.project.id} serviceName={chosen.service.name} />
    </>
  ) : null;

  if (runs.isPending) return <p className="muted">Loading runs…</p>;
  if (runs.error) return <p className="error">Could not load runs: {runs.error.message}</p>;
  if (runs.data.length === 0) {
    return (
      <>
        {trigger}
        <p className="muted">No runs yet.</p>
      </>
    );
  }

  return (
    <>
      {trigger}
      <table>
      <caption>Runs</caption>
      <thead>
        <tr>
          <th>Run</th>
          <th>Repository</th>
          <th>Date</th>
          <th>Status</th>
          <th>Reason</th>
          <th>Mode</th>
          <th className="num">Considered</th>
          <th className="num">Selected</th>
          <th className="num">Dispatched</th>
          <th className="num">Cost</th>
        </tr>
      </thead>
      <tbody>
        {runs.data.map((run) => (
          <tr key={run.id}>
            <td>
              <Link to={`/runs/${run.id}`}>{run.id.slice(0, 8)}</Link>
            </td>
            <td>{run.repo}</td>
            <td>{run.run_date}</td>
            <td>
              <Status value={run.status} />
            </td>
            <td className="muted">{run.reason ?? "—"}</td>
            <td>{run.dry_run ? "dry" : run.trigger}</td>
            <td className="num">{run.considered}</td>
            <td className="num">{run.selected}</td>
            {/* Selected and dispatched are separate columns on purpose:
                "selected 3, dispatched 0" is a run whose triage worked and
                whose dispatch did not. */}
            <td className={run.dispatched < run.selected ? "num warn" : "num"}>
              {run.dispatched}
            </td>
            <td className="num">{money(run.total_cost_usd)}</td>
          </tr>
        ))}
        </tbody>
      </table>
    </>
  );
}

export function Status({ value }: { value: string }) {
  return <span className={`status status-${value}`}>{value}</span>;
}
